import { createContext, useContext } from 'react'

import {
  Objectclass,
  Pagination,
  SortOption,
  YesObject
} from '@yes.technology/react-toolkit'
import { ConcatFields } from 'shared/utils/object/concatenateFields'
import { FieldValuesValue } from '../../FilterFields/types'
import { DataTableConfigProps } from '../DataTable'
import { CustomActionButtonProps, MapColumnToAction } from '../DataTables.types'
import {
  ActionColumn,
  ActionColumnsPosition,
  ColumnLayout,
  ExtraColumn,
  ExtraSelectionAction,
  SelectAndLinkOptions,
  SelectionAction,
  SelectionOption
} from '../types'

export type Mode = 'edit' | 'insert' | 'view'

type ChangedFieldValues = {
  [uuidOrIndex: string]: {
    [columnName: string]: FieldValuesValue
  }
}

export type TableContextValue = DataTableConfigProps & {
  idObjectClass?: string
  objectclass?: Objectclass
  fields?: Objectclass['fields']
  actionColumns: ActionColumn[]
  actionColumnsPosition: ActionColumnsPosition
  columnsNames: string[]
  readOnlyColumns: string[]
  objects: YesObject[]
  refetchObjects: () => void
  changedFieldValues: ChangedFieldValues
  setChangedFieldValues: (values: ChangedFieldValues) => void
  columnsLayouts: ColumnLayout[]
  extraColumns?: ExtraColumn[]
  onSort: (newSortOptions: SortOption[], isDefaultSorting?: boolean) => void
  sortOptions: SortOption[]
  onChangeColumnsPage: (page: number) => void
  currentColumnIndex: number
  totalColumns: number
  amountOfFixedColumns: number
  pagination?: Partial<Pagination>
  totalObjects?: number
  selectedRows: Record<string, SelectionOption>
  setSelectedRows: (rows: Record<string, SelectionOption>) => void
  mode: Mode
  setMode: (mode: Mode) => void
  relationCounterpartField?: {
    [columnName: string]: [
      {
        uuid: string
        des: string
      }
    ]
  }
  objectclassQueryString?: string
  displayLayoutSelection: boolean
  informational?: boolean
  embedded?: boolean
  navigationSliderBackgroundColor?: string
  navigationSliderColor?: string
  allowCollapsibleNavigationCardCustomColors?: boolean
  concatFields?: ConcatFields
  hideRowActionButtons?: boolean
  hideSortButtons?: boolean
  hideEditAction?: boolean
  amountOfActionColumns: number
  customActionButtons?: {
    [objectUuid: string]: CustomActionButtonProps
  }
  allColumns?: string[]
  selectAndLinkOptions?: SelectAndLinkOptions
  primarySelectionAction?: SelectionAction
  extraSelectionActions: ExtraSelectionAction[]
  isShowPDFReports?: boolean
  isShowXLSReport?: boolean
  disableLinkButton?: boolean
  allowHighlightSelectedRow?: boolean
  insertOnly?: boolean
  hideInsertAction: boolean
  hideSelectAndLinkAction: boolean
  hideDocumentUploadAction?: boolean
  mapColumnToAction?: MapColumnToAction
  highlightedRows?: string[]
  selectAndLinkTargetPath?: string
  textFading?: boolean
  uploadAcceptedFileTypes?: string
  extendMobileAreaLine?: boolean
  relationColumnName?: string
}

export const defaultContextValue: TableContextValue = {
  actionColumns: [],
  actionColumnsPosition: 'last',
  columnsNames: [],
  readOnlyColumns: [],
  objects: [],
  refetchObjects: () => '',
  changedFieldValues: {},
  setChangedFieldValues: () => '',
  columnsLayouts: [],
  extraColumns: [],
  onSort: () => undefined,
  sortOptions: [],
  currentColumnIndex: 0,
  totalColumns: 0,
  amountOfFixedColumns: 0,
  pagination: {
    offset: 0,
    limit: 0
  },
  totalObjects: 0,
  layoutClassificationUuid: '',
  hierarchyLevel: 'level_2',
  actionButton: {
    type: 'view',
    target: '_self',
    sitemodel: ''
  },
  selectedRows: {},
  setSelectedRows: () => undefined,
  mode: 'view',
  setMode: () => '',
  objectclassQueryString: 'uuid_objectclass',
  displayLayoutSelection: false,
  informational: false,
  embedded: false,
  navigationSliderBackgroundColor: undefined,
  navigationSliderColor: undefined,
  concatFields: undefined,
  hideRowActionButtons: false,
  hideSelectAndLinkAction: false,
  hideInsertAction: false,
  hideDocumentUploadAction: true,
  allowHighlightSelectedRow: true,
  onChangeColumnsPage: () => null,
  amountOfActionColumns: 0,
  extraSelectionActions: [],
  highlightedRows: []
}

const TableContext = createContext<TableContextValue>(defaultContextValue)

export { TableContext }

export default function useTableContext() {
  return useContext(TableContext)
}
